/**
 * Token Refresh Hook
 * Automatically refreshes access tokens before they expire
 *
 * - Access tokens expire after 4 hours (configurable in backend)
 * - Periodic refresh 10 minutes before expiry
 * - Wake-up refresh when user returns to tab/app after backgrounding
 * - Uses httpOnly cookies managed by the backend
 * - Handles refresh failures gracefully
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { apiClient, waitForRefreshConflict } from '@/lib/api/client';
import { broadcastAuthPurge, purgeAuthState } from '@/lib/auth/authLifecycle';
import type { UserSession } from '@/lib/server-fns/auth-fns';
import { logger } from '@/lib/utils/logger';
import { useSession } from './useAuthQuery';

// Refresh 10 minutes before token expiry (4h - 10min = 3h50min)
const REFRESH_INTERVAL_MS = (4 * 60 - 10) * 60 * 1000; // 3 hours 50 minutes
const RETRY_DELAY_MS = 60 * 1000; // 1 minute retry on failure
const MIN_WAKE_UP_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes — don't refresh on every visibility change

/**
 * Hook to manage automatic token refresh.
 * Subscribe to the session so the refresh interval (re)starts whenever the user
 * becomes authenticated — including the common client-side login flow (Telegram
 * OAuth sets the session via setQueryData + navigate, with no full page reload).
 */
export function useTokenRefresh() {
  const queryClient = useQueryClient();
  // Reactive session: re-runs the effect below on login / logout transitions.
  const { data: session } = useSession();
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshRef = useRef<number>(Date.now());

  const refreshToken = useCallback(async () => {
    logger.info('useTokenRefresh', 'Attempting to refresh access token');

    // Route through apiClient so this shares the isRefreshing mutex with
    // 401-triggered refreshes inside normal API calls. Previously this did a
    // raw fetch() with no coordination, so a proactive refresh could race an
    // apiClient refresh and, with refresh-token rotation, one would 401 and
    // spuriously log the user out. (audit M4)
    const outcome = await apiClient.refreshAuth();

    if (outcome.status === 'refreshed') {
      lastRefreshRef.current = Date.now();
      logger.info('useTokenRefresh', 'Access token refreshed successfully');
      return;
    }

    if (outcome.status === 'unauthorized') {
      // Refresh token invalid / revoked / missing — the session is gone
      // server-side. Purge the FULL cache + CSRF state (session included) so
      // private queries can't leak past the identity boundary, and tell
      // sibling tabs to do the same (#866). No retry: the user must
      // re-authenticate. Transient 'failed' / 'conflict' outcomes never reach
      // this branch, so an unavailable blip never purges.
      logger.warn('useTokenRefresh', 'Refresh token invalid; purging session');
      purgeAuthState(queryClient);
      broadcastAuthPurge();
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }

    if (outcome.status === 'conflict') {
      // A sibling tab/request won refresh-token rotation. Its Set-Cookie
      // response updates the browser-wide jar; do not clear the session or
      // immediately rotate again. Treat the winner as this interval's refresh.
      await waitForRefreshConflict(outcome.retryAfterMs);
      lastRefreshRef.current = Date.now();
      logger.info('useTokenRefresh', 'Refresh rotation conflict recovered');
      return;
    }

    // 'failed' — transient (5xx / network). Retry after a short delay.
    logger.warn('useTokenRefresh', 'Token refresh failed transiently, will retry');
    scheduleRetry();
  }, [queryClient]);

  const scheduleRetry = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }

    retryTimeoutRef.current = setTimeout(() => {
      refreshToken();
    }, RETRY_DELAY_MS);
  }, [refreshToken]);

  const startRefreshInterval = useCallback(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Set up periodic refresh
    refreshIntervalRef.current = setInterval(() => {
      refreshToken();
    }, REFRESH_INTERVAL_MS);

    logger.info(
      'useTokenRefresh',
      `Token refresh scheduled every ${REFRESH_INTERVAL_MS / 1000 / 60} minutes`
    );
  }, [refreshToken]);

  useEffect(() => {
    // H3: subscribe to the session. The interval + visibility listener only run
    // while authenticated. When a client-side login sets the session (the normal
    // flow), this effect re-runs and arms the interval — previously it read the
    // session once at mount and never re-ran, so those users got no proactive
    // refresh and were bounced to login at the 4h token expiry.
    if (!session) {
      return;
    }

    startRefreshInterval();

    // Wake-up refresh when user returns to tab after backgrounding
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
      if (timeSinceLastRefresh < MIN_WAKE_UP_INTERVAL_MS) {
        logger.debug(
          'useTokenRefresh',
          `Tab visible but last refresh was ${Math.round(timeSinceLastRefresh / 1000 / 60)}m ago (min: ${MIN_WAKE_UP_INTERVAL_MS / 1000 / 60}m), skipping`
        );
        return;
      }

      // Re-check auth from cache — the user may have logged out since mount.
      const currentSession = queryClient.getQueryData<UserSession>(['auth', 'session']);
      if (!currentSession) return;

      logger.info(
        'useTokenRefresh',
        `Tab visible after ${Math.round(timeSinceLastRefresh / 1000 / 60)}m, refreshing token`
      );
      refreshToken();

      // Restart the periodic interval (browser may have killed it)
      startRefreshInterval();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [session, queryClient, startRefreshInterval, refreshToken]);
}
